/**
 * Route access configuration that defines which routes are accessible to which user roles.
 * Routes not listed for a role are restricted to that role.
 * 
 * @example
 * LMP users can only access: /home, /home/my-teams, /home/contact-us, etc.
 * LMP users cannot access: /home/add-profile, /home/manage-access, etc.
 */

type RouteAccessKey = 'lmp' | 'member' | 'admin';

/**
 * Maps each role to the routes they are allowed to access.
 * Paths use exact matching for security (e.g., '/home/add-profile' !== '/home/add-profile/*').
 */
export const ROUTE_ACCESS_CONFIG: Record<RouteAccessKey, readonly string[]> = {
  lmp: [
    '/home',
    '/home/',
    '/home/my-teams',
    '/home/contact-us',
    '/home/select-ubt',
    '/home/search-teams',
    '/home/teams-reporting',
    '/home/path-to-performance',
    '/home/team-documents',
    '/home/projects/:id',
    '/home/history/:id',
    '/home/members/*',
    '/home/manage-access',
    '/home/manage-access/add-access',
  ],
  member: [
    '/home',
    '/home/',
    '/home/my-teams',
    '/home/contact-us',
    '/home/select-ubt',
    '/home/search-teams',
    '/home/teams-reporting',
    '/home/path-to-performance',
    '/home/team-documents',
    '/home/projects/:id',
    '/home/history/:id',
    '/home/members/*',
  ],
  admin: [
    // Admin has access to all routes
    '/home',
    '/home/',
    '/home/my-teams',
    '/home/contact-us',
    '/home/select-ubt',
    '/home/search-teams',
    '/home/teams-reporting',
    '/home/path-to-performance',
    '/home/team-documents',
    '/home/projects/:id',
    '/home/history/:id',
    '/home/members/*',
    '/home/add-profile',
    '/home/edit-ubt',
    '/home/manage-access',
    '/home/manage-access/add-access',
  ],
};

/**
 * Safe fallback routes where users are redirected when they attempt unauthorized access.
 * Prioritized by role for a better UX.
 */
export const SAFE_FALLBACK_ROUTES: Record<RouteAccessKey, string> = {
  lmp: '/home',
  member: '/home',
  admin: '/home',
};

/**
 * Check if a route is accessible by a given role.
 * Supports parameterized route matching (e.g., '/home/projects/:id').
 * Supports wildcard route matching (e.g., '/home/members/*').
 * 
 * @param pathname - The current pathname from the URL (e.g., '/home/projects/123')
 * @param role - The normalized user role (e.g., 'lmp', 'member', 'admin')
 * @returns true if the route is accessible, false otherwise
 */
export function isRouteAccessible(pathname: string, role: RouteAccessKey): boolean {
  const allowedRoutes = ROUTE_ACCESS_CONFIG[role];
  if (!allowedRoutes) return true; // Default to allow if role not found

  return allowedRoutes.some((allowedRoute) => {
    // Exact match (e.g., '/home')
    if (allowedRoute === pathname) return true;

    // Wildcard match (e.g., '/home/members/*' matches '/home/members/add')
    if (allowedRoute.endsWith('/*')) {
      const baseRoute = allowedRoute.slice(0, -2); // Remove '/*'
      if (pathname === baseRoute || pathname.startsWith(baseRoute + '/')) {
        return true;
      }
    }

    // Parameterized route match (e.g., '/home/projects/:id' matches '/home/projects/123')
    const paramPattern = allowedRoute.replaceAll(/:[^\s/]+/g, '[^/]+');
    const regex = new RegExp(`^${paramPattern}$`);
    return regex.test(pathname);
  });
}
